import { useContext, useMemo, useState } from 'react'
import { LootContext } from '../context'
import OptionBar from './OptionBar'
import RcLootCouncilCta from './RcLootCouncilCta'
import Select from 'app/frontend/base/components/Select'
import TeamWishlistEncounters from './TeamWishlistEncounters'
import TeamWishlistTable from './TeamWishlistTable'
import DifficultySelector from './DifficultySelector'
import { useTeamWishlist } from 'app/frontend/base/config/queries'
import { useTeam } from 'app/frontend/base/config/context/team'
import { Skeleton } from '@/components/ui/skeleton'
import { Clock } from 'lucide-react'
import TeamAgeModal from './TeamAgeModal'
import NoMemberWarning from './NoMemberWarning'
import Card from 'app/frontend/base/components/Card'

const TeamOverview = () => {
  const { team } = useTeam()
  const [viewingAge, setViewingAge] = useState(false)
  const { droptimizerConfigurations, selectedSeason, difficulty } =
    useContext(LootContext)
  const [configurationId, setConfigurationId] = useState<string>('all')
  const [view, setView] = useState<string>(
    localStorage.getItem('wishlistOverviewView') || 'encounters',
  )

  const { data: teamWishlist, isPending } = useTeamWishlist(
    team.id,
    difficulty,
    selectedSeason.id,
    configurationId === 'all' ? undefined : parseInt(configurationId),
  )

  const visibleMembers = useMemo(() => {
    return (
      team.members?.filter((member) => member.teamRank?.wishlist_visibility) ??
      []
    )
  }, [team.members])

  return (
    <div>
      <h2 className='flex justify-between items-center gap-4 flex-wrap'>
        <div className='flex items-center whitespace-nowrap'>
          Wishlist overview
        </div>
        <OptionBar>
          {droptimizerConfigurations.length > 1 && (
            <Select
              className='w-50 max-w-50 min-w-50'
              value={configurationId.toString()}
              setValue={(value) => {
                if (value) {
                  setConfigurationId(value)
                }
              }}
              options={[
                { value: 'all', label: 'All styles (combined)' },
              ].concat(
                droptimizerConfigurations.map((configuration) => ({
                  value: configuration.id.toString(),
                  label: configuration.name,
                })),
              )}
            />
          )}
        </OptionBar>
      </h2>

      {visibleMembers.length === 0 ? (
        <NoMemberWarning className='my-6' />
      ) : null}

      <RcLootCouncilCta />

      <DifficultySelector className='grow' />

      {isPending ? (
        <Skeleton className='h-160 mt-2' />
      ) : (
        <Card className='mt-2 relative'>
          <div className='flex justify-between items-center'>
            <div className='justify-center flex gap-2 grow pl-6 uppercase font-medium'>
              <span className='opacity-70'>View by</span>{' '}
              <span
                onClick={() => {
                  setView('encounters')
                  localStorage.setItem('wishlistOverviewView', 'encounters')
                }}
                className={`cursor-pointer transition-opacity ${
                  view === 'encounters'
                    ? 'text-foreground'
                    : 'opacity-30 hover:opacity-100'
                }`}
              >
                Encounter
              </span>
              <span className='opacity-70'>/</span>
              <span
                onClick={() => {
                  setView('characters')
                  localStorage.setItem('wishlistOverviewView', 'characters')
                }}
                className={`cursor-pointer transition-opacity ${
                  view === 'characters'
                    ? 'text-foreground'
                    : 'opacity-30 hover:opacity-100'
                }`}
              >
                Character
              </span>
            </div>

            <Clock
              className='cursor-pointer text-muted-foreground hover:text-foreground transition-colors'
              onClick={() => setViewingAge(true)}
            />
            <TeamAgeModal
              teamWishlist={teamWishlist}
              open={viewingAge}
              setOpen={setViewingAge}
            />
          </div>

          {!teamWishlist ? (
            <p>No wishlist data available</p>
          ) : view === 'encounters' ? (
            <TeamWishlistEncounters teamWishlist={teamWishlist} />
          ) : (
            <TeamWishlistTable teamWishlist={teamWishlist} />
          )}
        </Card>
      )}
    </div>
  )
}

export default TeamOverview
